import { useDispatch, useSelector } from 'react-redux';
import ApplicantLanding from './components/ApplicantLanding';
import ApplicantRegistration from './components/ApplicantRegistration';
import ApplicantLogin from './components/ApplicantLogin';
import { useQuery } from '../../pmui/transactions/Transactions';
import ApplicantPolicies from './components/ApplicantPolicies';
import ApplicantPetPolicies from './components/ApplicantPetPolicies';
import ApplicantPropertyAddress from './components/ApplicantPropertyAddress';
import { getCompany, getPropertyGroupDetails, selectPropertyGroupDetailsState } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { Fragment, useEffect, useState } from 'react';
import { getApplicationIntiteeInfoV2, selectApplicantsState } from './sagas/applicants/applicantsSlice';

export enum ApplicantPageStates {
  Landing = 'Landing',
  PropertyAddress = 'PropertyAddress',
  ApplicationPolicies = 'ApplicationPolicies',
  PetPolicies = 'PetPolicies',
  Login = 'Login',
  Registration = 'Registration',
}

export default function Applicant() {
  const dispatch = useDispatch();
  const query = useQuery();
  const companyUuid = query.get('company');
  const organization = query.get('organization');
  const applicationUuid = query.get('application');
  const coapplicantUuid = query.get('coapplicant');
  const hmac = query.get('hmac');
  const [pageState, setPageState] = useState<ApplicantPageStates>(ApplicantPageStates.Landing);
  const { propertyGroupDetails, isFetchingPropertyGroupDetails, companyInfo } = useSelector(selectPropertyGroupDetailsState);
  const { ApplicationInviteeDetailV2Dto, isFetchingApplicationInviteeInfoSuccess } = useSelector(selectApplicantsState);

  //#region  primary applicant stuff
  useEffect(() => {
    //this flow is for primary applicant landing on this page initially to start a net new app (url has organization or company uuid in it)
    //*We had to design this to be backwards compatible with older style links that pointed directly to the company instead of the property group.
    //try for propertyGroup first,
    if (organization) {
      dispatch({ type: getPropertyGroupDetails.type, payload: { propertyGroupUuid: organization, productTypeId: [2] } });
    } else if (companyUuid) {
      //then still look for companyuuid to support backwards comaptibility for all those links that already exist.
      dispatch({ type: getCompany.type, payload: { companyUuid: companyUuid } });
    }
  }, [companyUuid, organization, dispatch]);

  useEffect(() => {
    //still primary applicant flow. Check if we got back the company info yet for older links
    if (!organization && !!companyInfo?.DefaultPropertyGroupUuid && !isFetchingPropertyGroupDetails && !propertyGroupDetails?.Uuid) {
      //they didn't pass in PropGroupUuid, but we do have company info.
      dispatch({ type: getPropertyGroupDetails.type, payload: { propertyGroupUuid: companyInfo?.DefaultPropertyGroupUuid, productTypeId: [2] } });
    }
  }, [propertyGroupDetails, companyInfo, dispatch]);

  //#endregion primary applicant stuff

  //#region coapplicant invitee section
  useEffect(() => {
    //if we haven't yet got property group details, and this invited applicant info comes back, we know we need to fetch those property group details
    if (isFetchingApplicationInviteeInfoSuccess && ApplicationInviteeDetailV2Dto.OrganizationUuid && !propertyGroupDetails.Uuid) {
      dispatch({
        type: getPropertyGroupDetails.type,
        payload: { propertyGroupUuid: ApplicationInviteeDetailV2Dto.OrganizationUuid, productTypeId: [2] },
      });
    }
  }, [isFetchingApplicationInviteeInfoSuccess, dispatch]);

  useEffect(() => {
    if (applicationUuid && coapplicantUuid && hmac) {
      dispatch({ type: getApplicationIntiteeInfoV2.type, payload: { applicationUuid, coapplicantUuid, hmac } });
    }
  }, [applicationUuid, coapplicantUuid, hmac, dispatch]);
  //#endregion coapplicant invitee section

  return (
    <Fragment>
      {pageState === ApplicantPageStates.Landing ? (
        <ApplicantLanding setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} isCoApplicantFlow={!!coapplicantUuid} />
      ) : pageState === ApplicantPageStates.ApplicationPolicies ? (
        <ApplicantPolicies setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} isCoApplicantFlow={!!coapplicantUuid} />
      ) : pageState === ApplicantPageStates.PropertyAddress ? (
        <ApplicantPropertyAddress setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} />
      ) : pageState === ApplicantPageStates.PetPolicies ? (
        <ApplicantPetPolicies setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} isCoApplicantFlow={!!coapplicantUuid} />
      ) : pageState === ApplicantPageStates.Registration ? (
        <ApplicantRegistration query={query} setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} isCoApplicantFlow={!!coapplicantUuid} />
      ) : pageState === ApplicantPageStates.Login ? (
        <ApplicantLogin query={query} setPageState={(pageState: ApplicantPageStates) => setPageState(pageState)} />
      ) : (
        <></>
      )}
    </Fragment>
  );
}
